import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AdminQuickSaleWordCloud = ({ selectedPOS = 'all' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCloud = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const qs = selectedPOS && selectedPOS !== 'all' ? `?pointOfSaleId=${selectedPOS}` : '';
        const res = await axios.get(`/api/admin-stats/quick-sale-word-cloud${qs}` ,{
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data?.data || []);
      } catch (err) {
        setError('No se pudo obtener el análisis.');
      } finally {
        setLoading(false);
      }
    };
    fetchCloud();
  }, [selectedPOS]);

  const scaled = useMemo(() => {
    if (!data || data.length === 0) return [];
    const counts = data.map(d => d.count);
    const min = Math.min(...counts);
    const max = Math.max(...counts);

    const lerp = (a, b, t) => a + (b - a) * t;
    const lerpColor = (c1, c2, t) => {
      const p = (c) => [parseInt(c.slice(1,3),16), parseInt(c.slice(3,5),16), parseInt(c.slice(5,7),16)];
      const [r1,g1,b1] = p(c1); const [r2,g2,b2] = p(c2);
      const r = Math.round(lerp(r1, r2, t));
      const g = Math.round(lerp(g1, g2, t));
      const b = Math.round(lerp(b1, b2, t));
      return `rgb(${r}, ${g}, ${b})`;
    };

    const baseMin = 12, baseMax = 36; // rango base
    const topBoost = 6; // incremento adicional para la palabra top
    const fromColor = '#6b7280'; // gris
    const toColor = '#2563eb';   // azul primario

    // Paleta por frecuencia (baja→alta): azul → verde → lima → naranja → rojo
    const freqGradient = ['#0ea5e9', '#22c55e', '#84cc16', '#f59e0b', '#ef4444'];

    // Hash simple determinista para variar rotación/desplazamiento por palabra
    const hash = (s) => {
      let h = 5381;
      for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
      return Math.abs(h);
    };

    // ordenar para detectar top (sin mutar original)
    const sorted = [...data].sort((a,b) => b.count - a.count);
    const topWord = sorted[0]?.text;

    return data.map(d => {
      const t = max === min ? 1 : (d.count - min) / (max - min); // 0..1
      let fontSize = Math.round(lerp(baseMin, baseMax, t));
      // Color por frecuencia usando buckets
      const idx = Math.min(freqGradient.length - 1, Math.max(0, Math.floor(t * freqGradient.length)));
      const baseColor = freqGradient[idx];
      const color = baseColor || lerpColor(fromColor, toColor, t);
      // Rotación y desplazamiento vertical más notorios
      const r = hash(d.text) % 1000 / 1000; // 0..1 determinista por palabra
      const angle = (r * 50) - 25; // -25..25 deg
      const yOffset = Math.round((r * 20) - 10); // -10..10 px
      const z = 100 + Math.round(t * 100);
      const fontWeight = d.text === topWord ? 700 : 500;
      if (d.text === topWord) fontSize = Math.min(baseMax + topBoost, fontSize + topBoost);
      return { ...d, fontSize, color, angle, yOffset, zIndex: z, fontWeight };
    });
  }, [data]);

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: 16,
      background: '#fff',
      width: '100%'
    }}>
      {loading && <div>Cargando análisis...</div>}
      {!loading && error && <div className="error-message">{error}</div>}
      {!loading && !error && scaled.length === 0 && (
        <div style={{ color: '#6b7280' }}>Sin datos de descripciones para el mes actual.</div>
      )}
      {!loading && !error && scaled.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: 120
          }}
        >
          {scaled.map((w, idx) => (
            <span
              key={`${w.text}-${idx}`}
              title={`${w.text} (${w.count})`}
              style={{
                fontSize: w.fontSize,
                lineHeight: 1.1,
                color: w.color,
                display: 'inline-block',
                transform: `translateY(${w.yOffset || 0}px) rotate(${w.angle || 0}deg)`,
                zIndex: w.zIndex || 100,
                fontWeight: w.fontWeight || 500,
                wordBreak: 'break-word'
              }}
            >
              {w.text}
            </span>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
        Muestra palabras más frecuentes del mes para la Venta Rápida.
      </div>
    </div>
  );
};

export default AdminQuickSaleWordCloud;
